// PhaseDialog — shared base for the confirm/running/review/error modal pattern.
//
// Extracted from evaluate-dialog.tsx + diagnose-dialog.tsx (19BO+ refactor).
// Both shared a state-leak bug (S1): closing the modal while an async call is
// running left a pending Promise that resolved later and overwrote the reset
// phase, so reopening showed a stale review instead of the confirm step.
//
// This base fixes S1 with two mechanisms:
//   1. open→reset: useEffect resets phase to 'confirm' whenever `open` flips
//      false→true (the reopen always starts fresh).
//   2. cancel token: startAsync stamps a token; if the modal closes (token
//      bumps) before the Promise resolves, the resolution is discarded.
//
// Composition: render-prop. Consumers provide renderConfirm/renderReview/
// renderError; the base owns the shell (overlay/head/body/spinner/error) +
// phase state. This keeps evaluate/diagnose as thin callers and prevents the
// third clone (B5 / repair migration) from reintroducing the bug.
//
// CSS note: class names default to the `phase-dialog-*` prefix (the shared
// namespace defined in animations.css next to .repair-*). evaluate-dialog +
// diagnose-dialog are thin callers and inherit this default. repair-dialog
// passes overlayClassPrefix="repair" and keeps its own .repair-* classes
// (extra states not shared here). DEBT-19BO+-PHASE-DIALOG-CSS resolved.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { X, Sparkles, AlertTriangle } from 'lucide-react';
import { useT } from '../i18n/use-t.js';

/**
 * Context handed to renderConfirm. Lets the consumer kick off the async work
 * (which transitions phase → running → review/error) and close the modal.
 */
export interface PhaseDialogConfirmCtx {
  /** Start an async operation. On success → review phase (renderFn builds the
   *  review node from the resolved value). On throw → error phase.
   *  mapError (optional) converts the thrown value to a user-facing message;
   *  defaults to err.message. The cancel token ensures a resolution after
   *  close is discarded. */
  startAsync<T>(
    fn: () => Promise<T>,
    renderReviewValue: (value: T) => ReactNode,
    mapError?: (err: unknown) => string,
  ): void;
  /** Close the modal (resets to confirm + calls onClose). */
  close(): void;
}

export interface PhaseDialogProps<TReview> {
  open: boolean;
  onClose(): void;
  /** Dialog title (i18n key or literal — passed through useT if it's a key). */
  title: string;
  /** Aria label for the overlay (i18n key or literal). */
  ariaLabel?: string;
  /** Render the confirm-phase body. Receives ctx to start async / close. */
  renderConfirm(ctx: PhaseDialogConfirmCtx): ReactNode;
  /** Render the review-phase body from the resolved value + accept/close ctx. */
  renderReview(value: TReview, ctx: { accept(): void; close(): void }): ReactNode;
  /** Optional custom error body. Defaults to a standard message + retry. */
  renderError?(message: string, ctx: { retry(): void; close(): void }): ReactNode;
  /** Loading text shown in the running phase (i18n key or literal). */
  loadingLabel?: string;
  /** Cost-hint / retry i18n keys are consumer-supplied via renderConfirm/renderError. */
  /** CSS class prefix for the shell. Default 'evaluate' (matches existing dialogs). */
  overlayClassPrefix?: string;
  /** Header icon. Default Sparkles. */
  headerIcon?: ReactNode;
}

type Phase<TReview> =
  | { kind: 'confirm' }
  | { kind: 'running' }
  | { kind: 'review'; value: TReview }
  | { kind: 'error'; message: string };

export function PhaseDialog<TReview>({
  open,
  onClose,
  title,
  ariaLabel,
  renderConfirm,
  renderReview,
  renderError,
  loadingLabel,
  overlayClassPrefix = 'phase-dialog',
  headerIcon = <Sparkles size={16} aria-hidden="true" />,
}: PhaseDialogProps<TReview>): ReactNode {
  const t = useT();
  const [phase, setPhase] = useState<Phase<TReview>>({ kind: 'confirm' });
  // Cancel token: bumped on every close. startAsync captures the token at call
  // time; if it changed by the time the Promise resolves, discard the result.
  const cancelRef = useRef(0);

  // S1 fix #1: reset to confirm whenever the modal reopens, AND cancel any
  // in-flight async when it closes. Bumping the token on open change (either
  // direction) ensures a resolution arriving while closed is discarded — even
  // if the close came from the parent flipping `open` (not from close()).
  useEffect(() => {
    cancelRef.current += 1;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset phase to confirm whenever the modal reopens; key-reset alternative needs parent to own the open prop
    if (open) setPhase({ kind: 'confirm' });
  }, [open]);

  const startAsync = useCallback(
    <T,>(
      fn: () => Promise<T>,
      renderReviewValue: (value: T) => ReactNode,
      mapError?: (err: unknown) => string,
    ) => {
      const myToken = cancelRef.current;
      setPhase({ kind: 'running' });
      fn()
        .then((value) => {
          // S1 fix #2: discard if cancelled (modal closed) while in flight.
          if (cancelRef.current !== myToken) return;
          setPhase({ kind: 'review', value: value as unknown as TReview });
        })
        .catch((err) => {
          if (cancelRef.current !== myToken) return;
          const message = mapError ? mapError(err) : err instanceof Error ? err.message : '操作失败';
          setPhase({ kind: 'error', message });
        });
    },
    [],
  );

  const close = useCallback(() => {
    cancelRef.current += 1; // invalidate any in-flight resolution
    setPhase({ kind: 'confirm' });
    onClose();
  }, [onClose]);

  if (!open) return null;

  const p = overlayClassPrefix;

  const confirmCtx: PhaseDialogConfirmCtx = { startAsync, close };

  const reviewCtx = {
    accept: close, // accept = persist (consumer does it in renderReview) then close
    close,
  };

  const retry = () => {
    if (phase.kind !== 'error') return;
    // Re-run: the running phase stored nothing, so consumer must re-trigger.
    // Simplest correct behavior: reset to confirm and let user click again.
    setPhase({ kind: 'confirm' });
  };

  const loading = loadingLabel ?? '';
  const aria = ariaLabel ?? title;

  return (
    <div className={`${p}-overlay`} role="dialog" aria-modal="true" aria-label={aria}>
      <div className={`${p}-dialog`}>
        <header className={`${p}-dialog-head`}>
          {headerIcon}
          <h2>{title}</h2>
          <button
            type="button"
            className={`${p}-dialog-close`}
            onClick={close}
            aria-label={t('common.close')}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </header>

        {phase.kind === 'confirm' && (
          <div className={`${p}-dialog-body`}>
            {renderConfirm(confirmCtx)}
          </div>
        )}

        {phase.kind === 'running' && (
          <div className={`${p}-dialog-body ${p}-dialog-running`}>
            <div className={`${p}-dialog-spinner`} aria-hidden="true" />
            {loading ? <p>{loading}</p> : null}
          </div>
        )}

        {phase.kind === 'error' && (
          <div className={`${p}-dialog-body ${p}-dialog-error`}>
            <AlertTriangle size={20} aria-hidden="true" />
            <p>{phase.message}</p>
            {renderError
              ? renderError(phase.message, { retry, close })
              : (
                <div className={`${p}-dialog-actions`}>
                  <button type="button" className="btn-secondary" onClick={close}>
                    {t('common.close')}
                  </button>
                  <button type="button" className="btn-primary" onClick={retry}>
                    {t('common.retry')}
                  </button>
                </div>
              )}
          </div>
        )}
        {phase.kind === 'review' && (
          <div className={`${p}-dialog-body`}>
            {renderReview(phase.value, reviewCtx)}
          </div>
        )}
      </div>
    </div>
  );
}
